import React, { useRef, useState } from 'react';

/**
 * Parses a human-readable size string like "5MB" or "500KB" into bytes.
 * Returns null if the string cannot be parsed.
 */
function parseMaxSize(sizeStr) {
    if (!sizeStr) return null;
    const match = String(sizeStr).trim().match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB|B)?$/i);
    if (!match) return null;
    const value = parseFloat(match[1]);
    const unit = (match[2] || 'B').toUpperCase();
    const multipliers = { B: 1, KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
    return value * (multipliers[unit] ?? 1);
}

/**
 * UploadFieldType
 *
 * Renders a file picker for `input_type = "upload"` fields.
 * - On file pick: creates a local blob URL for instant iframe preview,
 *   and calls onFileSelected so the parent can store the pending File object.
 * - Validates file size against maxSize and restricts OS picker via acceptedTypes.
 * - Displays a thumbnail of the current value (blob URL or CDN URL on edit).
 *
 * Props:
 *   label          - field label
 *   helpText       - optional help text
 *   value          - current field value (blob URL or CDN URL)
 *   onChange       - called with synthetic event { target: { name, value: blobUrl } }
 *   onFileSelected - called with (fieldId, File) so parent can store pending upload
 *   id             - field id (used as the key in fieldValues / pendingUploads)
 *   name           - html name attribute
 *   error          - validation error string
 *   mandatory      - boolean
 *   maxSize        - string like "5MB" or "500KB" — max allowed file size
 *   acceptedTypes  - array of MIME types e.g. ["image/jpeg", "application/pdf"]
 */
const UploadFieldType = ({
    label,
    helpText,
    value,
    onChange,
    onFileSelected,
    id,
    name,
    error,
    mandatory,
    maxSize,
    acceptedTypes,
}) => {
    const fileInputRef = useRef(null);
    const [sizeError, setSizeError] = useState('');

    const maxSizeBytes = parseMaxSize(maxSize);
    const acceptAttr = acceptedTypes?.length ? acceptedTypes.join(',') : 'image/*';

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate MIME type against acceptedTypes (supports wildcards like "image/*")
        if (acceptedTypes?.length) {
            const isAllowed = acceptedTypes.some(type => {
                if (type.endsWith('/*')) {
                    // Wildcard match: "image/*" matches any "image/..." MIME type
                    const category = type.split('/')[0];
                    return file.type.startsWith(`${category}/`);
                }
                return file.type === type;
            });
            if (!isAllowed) {
                const readableTypes = acceptedTypes
                    .map(t => t.split('/').pop().replace('*', 'all'))
                    .join(', ')
                    .toUpperCase();
                setSizeError(`Invalid file type. Allowed: ${readableTypes}.`);
                e.target.value = '';
                return;
            }
        }

        // Validate file size
        if (maxSizeBytes !== null && file.size > maxSizeBytes) {
            setSizeError(`File exceeds maximum size of ${maxSize}.`);
            e.target.value = '';
            return;
        }
        setSizeError('');

        // Revoke any previous blob URL to free memory
        if (value && value.startsWith('blob:')) {
            URL.revokeObjectURL(value);
        }

        const blobUrl = URL.createObjectURL(file);

        // Update fieldValues with blob URL → triggers live iframe preview
        onChange({ target: { name: name || id, value: blobUrl } });

        // Notify parent to store the raw File for upload-on-save
        if (typeof onFileSelected === 'function') {
            onFileSelected(id, file);
        }
    };

    const isPreviewable = value && (value.startsWith('blob:') || value.startsWith('http') || value.startsWith('//'));
    const displayError = sizeError || error;

    // Build a readable hint for accepted types
    const typesHint = acceptedTypes?.length
        ? acceptedTypes.map(t => t.split('/').pop().replace('*', 'all')).join(', ').toUpperCase()
        : 'PNG, JPG, GIF, WebP';

    return (
        <div className="w-full">
            {/* Label */}
            <div className="flex items-center gap-1 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                    {label}
                    {mandatory && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            </div>

            {/* Help text */}
            {helpText && (
                <p className="text-xs text-gray-500 mb-2">{helpText}</p>
            )}

            {/* Upload area */}
            <div
                className={`relative border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors
                    ${displayError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-gray-50 hover:border-primary hover:bg-blue-50'}`}
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptAttr}
                    className="hidden"
                    id={`upload-input-${id}`}
                    name={name || id}
                    onChange={handleFileChange}
                />

                {isPreviewable ? (
                    /* Thumbnail when a file is selected / pre-filled */
                    <div className="flex flex-col items-center gap-2">
                        <img
                            src={value}
                            alt="preview"
                            className="max-h-32 max-w-full object-contain rounded"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="text-xs text-primary underline">Click to replace</span>
                    </div>
                ) : (
                    /* Placeholder when no file */
                    <div className="flex flex-col items-center gap-2 py-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12V4m0 0L8 8m4-4l4 4" />
                        </svg>
                        <span className="text-sm text-gray-500">Click to upload</span>
                        <span className="text-xs text-gray-400">{typesHint}</span>
                        {maxSize && (
                            <span className="text-xs text-gray-400">Max size: {maxSize}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Validation error */}
            {displayError && (
                <p className="mt-1 text-xs text-red-500">{displayError}</p>
            )}
        </div>
    );
};

export default UploadFieldType;
